import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { Sidebar, Topbar } from './components/Shell.jsx';
import { Icon } from './components/Icons.jsx';
import { AIPanel, VoiceMode } from './components/AIAssistant.jsx';
import { useTranslation } from 'react-i18next';
import { useAuth } from './contexts/AuthContext.jsx';
import { applyBrandLanguage } from './i18n';
import { Onboarding } from './screens/OnboardingChat.jsx';
import OnboardingFork from './screens/OnboardingFork.jsx';
import FreePublishingOnboardingGate from './components/freePublishing/FreePublishingOnboardingGate.jsx';
import OnboardingConnect from './screens/OnboardingConnect.jsx';
import OnboardingSayHello from './screens/OnboardingSayHello.jsx';
import { Dashboard } from './screens/Dashboard.jsx';
import { Library } from './screens/Library.jsx';
import { PostPreview } from './screens/PostPreview.jsx';
import { EditPost } from './screens/EditPost.jsx';
import FreePublishingEditorRoute from './components/freePublishing/FreePublishingEditorRoute.jsx';
import { Settings as NewSettings } from './screens/Settings.jsx';
import Plans from './screens/Plans.jsx';
import { CreatePost as NewCreatePost } from './screens/CreatePost.jsx';
import { ContentPlan as NewContentPlan } from './screens/ContentPlan.jsx';
import { EditStrategy as NewEditStrategy } from './screens/EditStrategy.jsx';
import { Calendar as NewCalendar } from './screens/Calendar.jsx';
import { Batch as NewBatch } from './screens/Batch.jsx';
import { Messages as NewMessages } from './screens/Messages.jsx';
import { Auth as NewAuth } from './screens/Auth.jsx';
import Logout from './screens/Logout.jsx';
import { Referrals as NewReferrals } from './screens/Referrals.jsx';
import { ReferralSignup } from './screens/ReferralSignup.jsx';
import VerifyEmailWall from './screens/VerifyEmailWall.jsx';
import RequireVerifiedEmail from './components/RequireVerifiedEmail.jsx';
import TrialCardCaptureContainer from './screens/TrialCardCaptureContainer.jsx';
import PlanSelectScreen from './screens/PlanSelectScreen.jsx';
import { cardGateEnabled, freePreviewClientEnabled, hostedTrialCheckoutEnabled, mcpOnboardingEnabled, previewModeEnabled } from './lib/flags';
import { usePlanCatalog } from './hooks/usePlanCatalog.js';
import { resolveManagedPaymentsNewSubscriptions } from './lib/managedPayments.js';
import { PreviewProvider } from './contexts/PreviewContext.jsx';
import { markOnboardingCompleted, onboardingJustCompleted } from './lib/onboardingCompletion.js';
import { safeReturnPath, rememberReturnPath, takeReturnPath } from './lib/returnPath.js';
import { clearOnboardingSkipped, onboardingWasSkipped } from './lib/onboardingSkip.js';
import ScreenTraceBridge from './components/ScreenTraceBridge.jsx';
import OnboardingNudge from './components/OnboardingNudge.jsx';
import ProductTour from './components/ProductTour.jsx';
import BillingStateBanner from './components/BillingStateBanner.jsx';
import { readMcpOnboardingChoice } from './lib/connectors.js';
import { ReferralWelcome } from './screens/ReferralWelcome.jsx';
import { AdminReferrals } from './screens/AdminReferrals.jsx';
import { AdminBroadcast } from './screens/AdminBroadcast.jsx';
import { AdminActivation } from './screens/AdminActivation.jsx';
import { AdminPlans } from './screens/AdminPlans.jsx';
import { AdminSupport } from './screens/AdminSupport.jsx';
import { AdminUsers } from './screens/AdminUsers.jsx';
import { AdminBehavior } from './screens/AdminBehavior.jsx';
import { AdminReplay } from './screens/AdminReplay.jsx';
import { AppShell } from './components/AppShell';
import { UploadProgressProvider } from './components/UploadProgress.jsx';
import { Scheduler } from './screens/Scheduler.jsx';
import { Content } from './screens/Content.jsx';
import { Create } from './screens/Create.jsx';
import { CarouselEditor } from './screens/Carousel.jsx';
import { HelpCenter, NotificationsPanel, NotFound } from './screens/Support.jsx';
import { Settings } from './screens/DesignScreens.jsx';
import { ContentPlanScreen } from './screens/content-plan/ContentPlanScreen.jsx';
import FirstPostReveal from './screens/FirstPostReveal.jsx';
import ResearchPrint from './screens/content-plan/ResearchPrint.jsx';
import ResearchScreen from './screens/ResearchScreen.jsx';
import BrandKitScreen from './screens/BrandKitScreen.jsx';
import ResearchActivityBridge from './components/ResearchActivityBridge.jsx';
import AddBrandChainBridge from './components/AddBrandChainBridge.jsx';
import PendingOAuthResume from './components/PendingOAuthResume.jsx';
import PendingOAuthBanner from './components/PendingOAuthBanner.jsx';
import { PendingOAuthProvider, usePendingOAuth } from './contexts/PendingOAuthContext.jsx';
import { UpgradeProvider } from './contexts/UpgradeContext.jsx';
import { BrandProvider } from './contexts/BrandContext.jsx';
import { track, trackOnce } from './lib/funnel.js';
import { followTrialStart } from './lib/gtag.js';
import { getFunnelSource } from './lib/funnelSource.js';
import { api } from './services/api/client.js';
import { ContentFeedProvider, useContentFeedContext } from './state/ContentFeedContext.jsx';
import { DashboardDataProvider } from './state/DashboardDataContext.jsx';
import { batchGenerationFailure } from './lib/generationFailure.js';
import { useDashboardData } from './state/DashboardDataContext.jsx';
import { GenerationStatusProvider, useGenerationStatus } from './state/GenerationStatusContext.jsx';
import { useContentFeed } from './hooks/useContentFeed.js';
import { cardGateDecision } from './lib/pendingOAuth.js';
import { ONBOARDING_SOCIAL_RETURN_KEY, browserStorage, isOnboardingSocialReturn, onboardingSocialResumePath } from './lib/onboardingSocialReturn.js';
import OnboardingSocialResume from './components/OnboardingSocialResume.jsx';
import { classifyBrandProfileError, resolveMissingBrand } from './lib/requireOnboarding.js';
import { isChatGptOrigin } from './lib/signupSource.js';
import { postingFeatureForPath, postingLockedSection } from './lib/postingAccess.js';
import PostingFeatureLock from './components/PostingFeatureLock.jsx';
import { NotInFreePreview, useFreePreview } from './contexts/FreePreviewContext.jsx';
import FreePreviewProvider from './components/freePreview/FreePreviewProvider.jsx';
import PreviewRouteOutlet from './components/freePreview/PreviewRouteOutlet.jsx';
import PreviewResumeHost from './components/freePreview/PreviewResumeHost.jsx';
import PostTrialOnboardingGate from './components/PostTrialOnboardingGate.jsx';
import PreviewStrip from './components/freePreview/PreviewStrip.jsx';
import {
  PostEditor,
  PostPreview as LegacyPostPreview
} from './screens/Misc.jsx';

const SCREEN_PATHS = {
  onboarding: '/onboarding',
  dashboard: '/app',
  scheduler: '/app/calendar',
  content: '/app/library',
  create: '/app/create',
  preview: '/app/preview',
  social: '/app/settings?tab=social',
  brand: '/app/plan',
  billing: '/app/settings?tab=billing',
  help: '/app/help',
  settings: '/app/settings'
};

const ACTIVATION_RETURN_KEY = 'activation_return_to_first_post';
const ASSISTANT_SOCIAL_RETURN_KEY = 'dathent_assistant_social_oauth_return';

const LEGACY_PATH_TO_SCREEN = {
  scheduler: 'scheduler',
  content: 'content',
  create: 'create',
  brand: 'brand',
  social: 'settings',
  billing: 'settings',
  dashboard: 'dashboard',
  preview: 'preview',
  help: 'help',
  settings: 'settings',
  // Pack route names (flat /app/<name>)
  calendar: 'scheduler',
  library: 'content',
  plan: 'brand',
  notifications: 'help',
  account: 'settings',
};

const SETTINGS_TAB_FOR_LEGACY_SCREEN = {
  billing: 'billing',
  social: 'social',
};

function screenFromLocation(location) {
  const params = new URLSearchParams(location.search);
  const normalizedPath = location.pathname.replace(/^\/app(?=\/|$)/, '') || '/';
  if (normalizedPath.startsWith('/settings/integrations') || (normalizedPath === '/' && params.has('provider'))) {
    return 'settings';
  }

  const segments = normalizedPath.replace(/^\/+/, '').split('/');
  const [firstSegment, contentId, contentAction] = segments;
  if (firstSegment === '') return null;
  if (firstSegment === 'onboarding') return 'onboarding';
  if (firstSegment === 'content' && contentId && contentAction === 'preview') return 'preview';
  if (firstSegment === 'content' && contentId && contentAction === 'edit') return 'editor';
  if (firstSegment === 'content' && contentId && contentAction === 'carousel') return 'carousel';
  if (firstSegment === 'posts') {
    if (contentId === 'create') return 'create';
    if (contentId === 'library') return 'content';
    if (contentId === 'calendar') return 'scheduler';
    return 'notFound';
  }
  if (firstSegment === 'content-plan') return 'brand';
  return LEGACY_PATH_TO_SCREEN[firstSegment] || 'notFound';
}

function contentIdFromLocation(location) {
  const normalizedPath = location.pathname.replace(/^\/app(?=\/|$)/, '') || '/';
  const [firstSegment, contentId, contentAction] = normalizedPath.replace(/^\/+/, '').split('/');
  return firstSegment === 'content' && contentId && ['preview', 'edit', 'carousel'].includes(contentAction) ? decodeURIComponent(contentId) : null;
}

function LoadingScreen() {
  return (
    <div className="onboard-page">
      <div className="card" style={{width:'min(420px, calc(100vw - 32px))', textAlign:'center'}}>
        <div className="ai-orb" style={{margin:'0 auto 16px'}}/>
        <h2 className="h2" style={{marginBottom:8}}>Loading Dathent</h2>
        <div className="muted">Checking your session...</div>
      </div>
    </div>
  );
}

function AuthAside({ heading, sub }) {
  return (
    <aside className="auth-aside">
      <div className="auth-aside-bg" />
      <div className="auth-orb" />
      <div className="auth-aside-content auth-brand">
        <div className="auth-logo">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 2 4 7v10l8 5 8-5V7zM4 7l8 5 8-5M12 22V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <span>Dathent</span>
      </div>
      <div className="auth-aside-content" style={{ maxWidth: 380 }}>
        <div className="eyebrow" style={{ color: 'var(--p-200)', marginBottom: 12 }}>AI Growth Autopilot</div>
        <h2 className="h1" style={{ fontSize: 34, marginBottom: 12 }}>{heading || 'Turn content into sales growth on autopilot'}</h2>
        <p className="body" style={{ color: 'var(--text-2)', margin: 0 }}>{sub || 'Dathent builds your strategy, generates posts, and publishes to connected social channels automatically.'}</p>
      </div>
    </aside>
  );
}

function AuthScreen({ mode }) {
  const isSignup = mode === 'signup';
  const { login, verifyMfaLogin, verifyMfaBackupCode, sendMfaEmailCode, verifyMfaEmailCode, verifyRequiredMfaSetup, reloadUser, isAuthenticated, loading, error } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaBackupCode, setMfaBackupCode] = useState('');
  const [mfaEmailCode, setMfaEmailCode] = useState('');
  const [mfaEmailSent, setMfaEmailSent] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [mfaSetupChallenge, setMfaSetupChallenge] = useState(null);
  const [mfaSetupQrDataUrl, setMfaSetupQrDataUrl] = useState(null);
  const [mfaSetupCode, setMfaSetupCode] = useState('');
  const [mfaSetupBackupCodes, setMfaSetupBackupCodes] = useState(null);
  const [mfaSetupCodesSaved, setMfaSetupCodesSaved] = useState(false);
  const [mfaMode, setMfaMode] = useState('totp');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState(null);
  const [mfaNotice, setMfaNotice] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      const from = location.state?.from;
      const target = safeReturnPath(from?.pathname, from?.search) || takeReturnPath() || '/';
      navigate(target, { replace: true });
    }
  }, [isAuthenticated, loading, location.state, navigate]);

  useEffect(() => {
    setFormError(null);
    setMfaChallenge(null);
    setMfaSetupChallenge(null);
    setMfaSetupQrDataUrl(null);
    setMfaSetupCode('');
    setMfaSetupBackupCodes(null);
    setMfaSetupCodesSaved(false);
    setMfaCode('');
    setMfaBackupCode('');
    setMfaEmailCode('');
    setMfaEmailSent(false);
    setMfaMode('totp');
    setMfaNotice(null);
  }, [isSignup]);

  async function submit(event) {
    event.preventDefault();
    setFormError(null);

    if (mfaSetupChallenge) {
      if (mfaSetupBackupCodes) {
        if (!mfaSetupCodesSaved) {
          setFormError('Save your backup codes before entering the platform.');
          return;
        }
        await reloadUser();
        navigate('/', { replace: true });
        return;
      }
      if (!/^\d{6}$/.test(mfaSetupCode)) {
        setFormError('Enter a valid 6-digit code.');
        return;
      }
      try {
        setSubmitting(true);
        const result = await verifyRequiredMfaSetup(mfaSetupChallenge.attemptId, mfaSetupCode);
        setMfaSetupBackupCodes(result.backupCodes || []);
      } catch (err) {
        setFormError(err.message || 'Unable to enable two-factor authentication');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (mfaChallenge) {
      try {
        setSubmitting(true);
        if (mfaMode === 'backup') {
          await verifyMfaBackupCode(mfaChallenge.attemptId, mfaBackupCode.trim());
        } else if (mfaMode === 'email') {
          await verifyMfaEmailCode(mfaChallenge.attemptId, mfaEmailCode.trim());
        } else {
          await verifyMfaLogin(mfaChallenge.attemptId, mfaCode.trim());
        }
        navigate('/', { replace: true });
      } catch (err) {
        setFormError(err.message || 'Unable to verify two-factor code');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    setSubmitting(true);
    try {
      const result = await login(email.trim(), password);
      if (result?.requiresMfa) {
        setMfaChallenge(result);
        setMfaMode('totp');
        setMfaCode('');
        setMfaBackupCode('');
        setMfaEmailCode('');
        setMfaEmailSent(false);
        setMfaNotice(null);
        return;
      }
      if (result?.requiresMfaSetup) {
        setMfaSetupChallenge(result);
        setMfaSetupCode('');
        setMfaSetupBackupCodes(null);
        setMfaSetupCodesSaved(false);
        try {
          setMfaSetupQrDataUrl(await QRCode.toDataURL(result.otpauthUrl, { width: 196, margin: 1, color: { dark: '#111111', light: '#ffffff' } }));
        } catch {
          setMfaSetupQrDataUrl(null);
        }
        return;
      }
      navigate('/', { replace: true });
    } catch (err) {
      setFormError(err.message || 'Authentication failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function copyMfaSetupText(value) {
    await navigator.clipboard?.writeText(value);
  }

  function downloadMfaSetupBackupCodes() {
    const codes = mfaSetupBackupCodes || [];
    const blob = new Blob([codes.join('\n') + '\n'], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'dathent-backup-codes.txt';
    link.click();
    URL.revokeObjectURL(url);
  }

  async function requestMfaEmailCode() {
    if (!mfaChallenge?.attemptId) return;
    try {
      setSubmitting(true);
      setFormError(null);
      setMfaNotice(null);
      await sendMfaEmailCode(mfaChallenge.attemptId);
      setMfaEmailSent(true);
      setMfaMode('email');
      setMfaEmailCode('');
      setMfaNotice(`We sent a login code to ${mfaChallenge.emailHint || 'your email'}.`);
    } catch (err) {
      setFormError(err.message || 'Unable to send email login code');
    } finally {
      setSubmitting(false);
    }
  }

  const mfaChallengeSubmitDisabled = submitting || loading || (
    mfaMode === 'totp'
      ? mfaCode.length !== 6
      : mfaMode === 'email'
        ? mfaEmailCode.length !== 6
        : !mfaBackupCode.trim()
  );

  return (
    <div className="auth-shell">
      <AuthAside
        heading={isSignup ? 'Private beta for growth teams' : undefined}
        sub={isSignup ? 'Public signup is paused while we onboard new workspaces manually.' : undefined}
      />
      <div className="auth-form-side cosmic-bg">
        <form className="auth-form-card slide-up" onSubmit={submit}>
          {mfaSetupChallenge ? (
            <>
              {!mfaSetupBackupCodes ? (
                <>
                  <h1 className="h1" style={{ marginBottom: 6 }}>Set up two-factor authentication</h1>
                  <p className="body" style={{ color: 'var(--text-3)', marginBottom: 24 }}>
                    Your workspace requires an authenticator app before you can enter Dathent.
                  </p>
                  {mfaSetupQrDataUrl ? <div className="mfa-qr" style={{ margin: '0 auto 16px' }}><img src={mfaSetupQrDataUrl} alt="Authenticator app QR code" /></div> : null}
                  {mfaSetupChallenge.manualKey && (
                    <div className="mfa-manual-key">
                      <div>
                        <div className="settings-section-label" style={{ margin: 0 }}>Can’t scan the QR code?</div>
                        <p className="caption" style={{ margin: '4px 0 10px' }}>Enter this setup key manually in your authenticator app.</p>
                      </div>
                      <code>{mfaSetupChallenge.manualKey}</code>
                      <button className="btn btn-secondary btn-sm" type="button" onClick={() => copyMfaSetupText(mfaSetupChallenge.manualKey)}>Copy setup key</button>
                    </div>
                  )}
                  <label className="label" style={{ marginTop: 16 }}>6-digit code</label>
                  <input className="input" inputMode="numeric" pattern="[0-9]*" value={mfaSetupCode} onChange={event => setMfaSetupCode(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" autoComplete="one-time-code" maxLength={6} required />
                  {(formError || error) && <div className="auth-error">{formError || error}</div>}
                  <button className="btn btn-primary btn-block btn-lg" type="submit" disabled={submitting || loading || mfaSetupCode.length !== 6} style={{ marginTop: 24 }}>
                    {submitting ? 'Verifying...' : 'Verify & enable 2FA'}
                  </button>
                  <button type="button" className="btn-ghost" style={{ background: 'none', border: 0, color: 'var(--text-3)', cursor: 'pointer', fontSize: 12, fontWeight: 600, marginTop: 18 }} onClick={() => { setMfaSetupChallenge(null); setMfaSetupCode(''); setMfaSetupBackupCodes(null); setFormError(null); }}>
                    Back to login
                  </button>
                </>
              ) : (
                <>
                  <h1 className="h1" style={{ marginBottom: 6 }}>Save your backup codes</h1>
                  <p className="body" style={{ color: 'var(--text-3)', marginBottom: 18 }}>
                    Store these codes somewhere safe. Each code works once if you lose your authenticator app.
                  </p>
                  <div className="mfa-backup-grid">{mfaSetupBackupCodes.map(codeValue => <code key={codeValue}>{codeValue}</code>)}</div>
                  <div className="row" style={{ gap: 8, marginTop: 14 }}>
                    <button className="btn btn-secondary btn-block" type="button" onClick={() => copyMfaSetupText(mfaSetupBackupCodes.join('\n'))}>Copy all codes</button>
                    <button className="btn btn-secondary btn-block" type="button" onClick={downloadMfaSetupBackupCodes}>Download codes</button>
                  </div>
                  <label className="row" style={{ gap: 8, marginTop: 18, alignItems: 'flex-start', cursor: 'pointer' }}>
                    <input type="checkbox" checked={mfaSetupCodesSaved} onChange={event => setMfaSetupCodesSaved(event.target.checked)} style={{ accentColor: 'var(--p-500)', width: 16, height: 16, marginTop: 2 }} />
                    <span className="caption">I have saved these codes.</span>
                  </label>
                  {(formError || error) && <div className="auth-error">{formError || error}</div>}
                  <button className="btn btn-primary btn-block btn-lg" type="submit" disabled={!mfaSetupCodesSaved} style={{ marginTop: 18 }}>Enter Dathent</button>
                </>
              )}
            </>
          ) : mfaChallenge ? (
            <>
              <h1 className="h1" style={{ marginBottom: 6 }}>Two-factor authentication</h1>
              <p className="body" style={{ color: 'var(--text-3)', marginBottom: 28 }}>
                {mfaMode === 'backup'
                  ? 'Enter one of your saved backup codes.'
                  : mfaMode === 'email'
                    ? `Enter the 6-digit code sent to ${mfaChallenge.emailHint || 'your email'}.`
                    : 'Enter the 6-digit code from your authenticator app.'}
              </p>

              {mfaMode === 'backup' ? (
                <>
                  <label className="label">Backup code</label>
                  <input className="input" value={mfaBackupCode} onChange={event => setMfaBackupCode(event.target.value.toUpperCase())} placeholder="ABCD-1234" autoComplete="one-time-code" required />
                </>
              ) : mfaMode === 'email' ? (
                <>
                  <label className="label">Email code</label>
                  <input className="input" inputMode="numeric" pattern="[0-9]*" value={mfaEmailCode} onChange={event => setMfaEmailCode(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" autoComplete="one-time-code" maxLength={6} required />
                </>
              ) : (
                <>
                  <label className="label">Authenticator code</label>
                  <input className="input" inputMode="numeric" pattern="[0-9]*" value={mfaCode} onChange={event => setMfaCode(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" autoComplete="one-time-code" maxLength={6} required />
                </>
              )}

              {mfaNotice && <div className="auth-success" style={{ marginTop: 12 }}>{mfaNotice}</div>}
              {(formError || error) && <div className="auth-error">{formError || error}</div>}

              <button className="btn btn-primary btn-block btn-lg" type="submit" disabled={mfaChallengeSubmitDisabled} style={{ marginTop: 24 }}>
                {submitting ? 'Verifying...' : 'Verify'}
              </button>

              <div className="row between" style={{ marginTop: 18, gap: 12, flexWrap: 'wrap' }}>
                {mfaMode !== 'totp' && (
                  <button type="button" className="btn-ghost" style={{ background: 'none', border: 0, color: 'var(--p-300)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }} onClick={() => { setMfaMode('totp'); setFormError(null); setMfaNotice(null); }}>
                    Use authenticator code
                  </button>
                )}
                {mfaMode !== 'backup' && (
                  <button type="button" className="btn-ghost" style={{ background: 'none', border: 0, color: 'var(--p-300)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }} onClick={() => { setMfaMode('backup'); setFormError(null); setMfaNotice(null); }}>
                    Use backup code
                  </button>
                )}
                {mfaChallenge.emailCodeAvailable && (
                  <button type="button" className="btn-ghost" style={{ background: 'none', border: 0, color: 'var(--p-300)', cursor: submitting ? 'default' : 'pointer', fontSize: 12, fontWeight: 600 }} onClick={requestMfaEmailCode} disabled={submitting}>
                    {mfaEmailSent ? 'Resend email code' : 'Email me a login code'}
                  </button>
                )}
                <button type="button" className="btn-ghost" style={{ background: 'none', border: 0, color: 'var(--text-3)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }} onClick={() => { setMfaChallenge(null); setMfaCode(''); setMfaBackupCode(''); setMfaEmailCode(''); setMfaEmailSent(false); setMfaNotice(null); setFormError(null); }}>
                  Back to login
                </button>
              </div>
            </>
          ) : isSignup ? (
            <EarlyAccessSignup onLogin={() => navigate('/login')} />
          ) : (
            <>
          <h1 className="h1" style={{ marginBottom: 6 }}>Welcome back</h1>
          <p className="body" style={{ color: 'var(--text-3)', marginBottom: 28 }}>Log in to continue to your workspace.</p>

          <label className="label">Email</label>
          <input className="input" type="email" value={email} onChange={event => setEmail(event.target.value)} placeholder="you@company.com" autoComplete="email" required />

          <div className="row between" style={{ marginTop: 18, marginBottom: 8 }}>
            <label className="label" style={{ margin: 0 }}>Password</label>
            <button className="btn-ghost" type="button" style={{ background: 'none', border: 0, fontSize: 12, color: 'var(--p-300)', cursor: 'pointer' }} onClick={() => navigate('/forgot')}>
              Forgot password?
            </button>
          </div>
          <div style={{ position: 'relative' }}>
            <input className="input" type={showPassword ? 'text' : 'password'} value={password} onChange={event => setPassword(event.target.value)} placeholder="At least 8 characters" autoComplete="current-password" minLength={8} required />
            <button type="button" onClick={() => setShowPassword(value => !value)} aria-label="Toggle password" style={{ position: 'absolute', right: 10, top: 10, background: 'none', border: 0, color: 'var(--text-3)', padding: 6, cursor: 'pointer' }}>
              <Icon name={showPassword ? 'eyeOff' : 'eye'} size={16} />
            </button>
          </div>

          {(formError || error) && <div className="auth-error">{formError || error}</div>}

          <button className="btn btn-primary btn-block btn-lg" type="submit" disabled={submitting || loading} style={{ marginTop: 24 }}>
            {submitting ? 'Please wait...' : 'Continue'}
          </button>

          <div className="row" style={{ gap: 12, margin: '24px 0', color: 'var(--text-3)' }}>
            <div className="divider" style={{ flex: 1 }} />
            <span style={{ fontSize: 12 }}>email auth is live</span>
            <div className="divider" style={{ flex: 1 }} />
          </div>

          <p className="caption" style={{ textAlign: 'center', marginTop: 0 }}>
            Need access?{' '}
            <button type="button" onClick={() => navigate('/signup')} style={{ background: 'none', border: 0, color: 'var(--p-300)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              Request early access
            </button>
          </p>
            </>
          )}
        </form>
        <footer className="auth-foot">
          <a href="https://dathent.ai/privacy" target="_blank" rel="noopener noreferrer">Privacy</a>
          <span className="auth-foot-sep">·</span>
          <a href="https://dathent.ai/terms" target="_blank" rel="noopener noreferrer">Terms</a>
          <span className="auth-foot-sep">·</span>
          <a href="https://dathent.ai/security" target="_blank" rel="noopener noreferrer">Security</a>
        </footer>
      </div>
    </div>
  );
}

function EarlyAccessSignup() {
  // Skip the in-app "early access only" interstitial — we never want a
  // visitor who reached the signup URL to land on a click-through page.
  // Redirect immediately to the public pre-registration form.
  // See pages/app-lending/public/pre-register.html.
  useEffect(() => {
    window.location.replace('https://dathent.ai/pre-register');
  }, []);
  return (
    <p className="body" style={{ color: 'var(--text-3)' }}>
      Redirecting to pre-registration…
    </p>
  );
}

function AuthUtilityScreen({ mode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(mode === 'confirm');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const resetToken = new URLSearchParams(location.search).get('token') || '';

  const content = {
    forgot: {
      title: sent ? 'Check your email' : 'Reset your password',
      body: sent ? `If ${email || 'your email'} exists, a reset link was sent.` : "Enter your email and we'll send a password reset link.",
      icon: 'mail',
    },
    reset: {
      title: sent ? 'Password updated' : 'Create new password',
      body: sent ? 'Your password was reset. Use the new password to log in.' : 'Choose a new password for your Dathent account.',
      icon: 'lock',
    },
    confirm: {
      title: 'Confirm your email',
      body: 'Open the confirmation link from your inbox, then return to Dathent.',
      icon: 'mail',
    },
  }[mode];

  async function submitForgot(event) {
    event.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      await api.auth.forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      setFormError(err.message || 'Unable to send reset link');
    } finally {
      setSubmitting(false);
    }
  }

  async function submitReset(event) {
    event.preventDefault();
    setFormError(null);
    if (!resetToken) {
      setFormError('This reset link is missing a token. Request a new link.');
      return;
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await api.auth.resetPassword(resetToken, password);
      setSent(true);
      setPassword('');
      setConfirmPassword('');
    } catch (err) {
      setFormError(err.message || 'Unable to reset password');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-shell">
      <AuthAside />
      <div className="auth-form-side cosmic-bg">
        <div className="auth-form-card slide-up">
          <button className="btn btn-ghost btn-sm" style={{ padding: '6px 10px', marginBottom: 24, marginLeft: -10 }} onClick={() => navigate('/login')}>
            <Icon name="arrowLeft" size={14} /> Back to login
          </button>
          <div style={{ width: 56, height: 56, marginBottom: 20, borderRadius: 14, background: 'rgba(124,92,255,0.12)', border: '1px solid rgba(124,92,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--p-300)' }}>
            <Icon name={content.icon} size={24} />
          </div>
          <h1 className="h1" style={{ marginBottom: 6 }}>{content.title}</h1>
          <p className="body" style={{ color: 'var(--text-3)', marginBottom: 28 }}>{content.body}</p>

          {mode === 'forgot' && !sent && (
            <form onSubmit={submitForgot}>
              <label className="label">Email</label>
              <input className="input" type="email" value={email} onChange={event => setEmail(event.target.value)} placeholder="you@company.com" autoComplete="email" required />
              {formError && <div className="auth-error">{formError}</div>}
              <button className="btn btn-primary btn-block btn-lg" type="submit" disabled={submitting} style={{ marginTop: 24 }}>{submitting ? 'Sending...' : 'Send reset link'}</button>
            </form>
          )}

          {mode === 'reset' && !sent && (
            <form onSubmit={submitReset}>
              {!resetToken && <div className="auth-note">This reset link is missing a token. Request a new password reset email.</div>}
              <label className="label">New password</label>
              <input className="input" type="password" value={password} onChange={event => setPassword(event.target.value)} placeholder="At least 8 characters" minLength={8} autoComplete="new-password" required />
              <label className="label" style={{ marginTop: 18 }}>Confirm password</label>
              <input className="input" type="password" value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} placeholder="Re-enter password" minLength={8} autoComplete="new-password" required />
              {formError && <div className="auth-error">{formError}</div>}
              <button className="btn btn-primary btn-block btn-lg" type="submit" disabled={submitting || !resetToken} style={{ marginTop: 24 }}>{submitting ? 'Updating...' : 'Update password'}</button>
            </form>
          )}

          {(sent || mode === 'confirm') && (
            <button className="btn btn-primary btn-block btn-lg" onClick={() => navigate('/login')}>Back to login</button>
          )}
        </div>
      </div>
    </div>
  );
}

function RequireAuth({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) {
    // Router state (`state.from`) is the primary carrier of "where to return
    // after login", but it's lost on a full-page round trip (a reload on
    // /login, or the Google OIDC hop through auth-worker back to /login?...).
    // Back it up in sessionStorage so the post-login redirect still has a
    // path to fall back to — see lib/returnPath.js.
    const backup = safeReturnPath(location.pathname, location.search);
    if (backup) rememberReturnPath(backup);
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return children;
}

function AuthenticatedRoutes() {
  const location = useLocation();
  const appRoute = location.pathname.startsWith('/app');
  return (
    <RequireAuth>
      <PendingOAuthProvider>
        <PendingOAuthResume />
        {/* /app renders this from RequireActiveBilling only after its decision,
            keeping the waiting banner off the mandatory card-capture wall. */}
        {!appRoute && <PendingOAuthBanner floating />}
        <Outlet />
      </PendingOAuthProvider>
    </RequireAuth>
  );
}

// Card gate (Model B) — held AFTER onboarding, BEFORE the app. A non-exempt org
// without an active trial/subscription is shown the card-capture wall. The CLIENT
// gate is dark by default (cardGateEnabled, localStorage 'dathent.cardGate'); the
// REAL enforcement is the gateway's CARD_GATE_REQUIRED, so feature APIs can't be
// used by skipping the SPA. Fail-OPEN everywhere (flag off, exempt org, any fetch
// error → let through) so existing/paying users are never trapped. On payment we
// poll entitlements until the webhook lands, then optimistically proceed.
//
// TASK-331: cardGateDecision (lib/pendingOAuth.js) now gates ONLY accessState
// === 'new' — a lapsed/grace/cancel_scheduled org allows straight through here
// (this fetch/poll shape is otherwise UNCHANGED). The persistent grace/lapsed/
// cancel_scheduled notice is <BillingStateBanner/>, mounted further down this
// file inside UpgradeProvider (it reuses the shared UpgradeModal for Renew, and
// UpgradeModal needs both DashboardDataProvider and UpgradeProvider above it —
// both sit inside `children` here, not beside PendingOAuthBanner).
export function RequireActiveBilling({ children }) {
  const { user, reloadUser } = useAuth();
  const { updateEntitlements } = usePendingOAuth();
  // GH-912: the stripe-worker's Managed Payments flag for new subscriptions
  // (GET /api/billing/plans) — see the gate render below.
  const { managedPaymentsNewSubscriptions: catalogManagedPayments } = usePlanCatalog();
  const location = useLocation();
  // Fill brand can enroll while this parent stays mounted on /app. The URL
  // requests a fresh decision, never grants access. Include the navigation key
  // so retrying from an existing ?preview=1 URL also rechecks the server.
  const previewEntryKey = new URLSearchParams(location.search).get('preview') === '1' ? location.key : null;
  const paidPollStarted = React.useRef(false);
  const [decision, setDecision] = useState(() => (cardGateEnabled() ? 'pending' : 'allow'));
  // GH-912: the entitlements read behind the gate decision, for its
  // managedPaymentsNewSubscriptions mirror.
  const [gateEntitlements, setGateEntitlements] = useState(null);
  // GH-1130: the trial-start follow-up (see handlePaid) reads entitlements in
  // the background after 'allow'; it stops when this gate unmounts (sign-out,
  // account switch). Reset on mount so a StrictMode remount keeps it running.
  const trialFollowStopped = React.useRef(false);
  useEffect(() => {
    trialFollowStopped.current = false;
    return () => { trialFollowStopped.current = true; };
  }, []);

  useEffect(() => {
    // cardGateEnabled() is a static per-session read (localStorage); a mid-session
    // console toggle needs a reload. Flag dark → decision already 'allow' from
    // init, so skip the fetch entirely (no redundant render).
    if (!cardGateEnabled()) return;
    const checkoutReturn = new URLSearchParams(location.search);
    if (checkoutReturn.get('checkout') === 'success' || checkoutReturn.has('session_id')) {
      // A hosted-checkout return can beat customer.subscription.created. Reuse
      // the activation poll instead of treating checkout_pending as a reason to
      // show the gate again; handlePaid fails open after the same 8 attempts.
      handlePaid();
      return;
    }
    let cancelled = false;
    const failOpen = setTimeout(() => { if (!cancelled) setDecision('allow'); }, 4000);
    (async () => {
      try {
        const ent = await api.billing.getEntitlements(previewEntryKey != null ? { fresh: true } : undefined);
        if (cancelled) return;
        updateEntitlements(ent);
        setGateEntitlements(ent);
        // paymentIssue (past_due/incomplete/unpaid) users ENTER the app on Free
        // limits + the Settings payment banner — their path is paying the open
        // invoice, NOT re-binding a card into a new trial. Gating them looped
        // forever: the setup-intent endpoint short-circuits "active" for a live
        // past_due sub -> onComplete -> reloadUser -> gate remounts -> repeat.
        // Preview remains an onboarding/overlay mode, never a billing bypass.
        // GH-1133 S5: a granted preview-arm account gets 'preview' (the
        // free-preview board) instead of the wall; see cardGateDecision.
        setDecision(cardGateDecision(ent, { previewMode: previewModeEnabled(), freePreview: freePreviewClientEnabled() }));
      } catch {
        if (!cancelled) setDecision('allow'); // fail-open: never trap on a fetch error
      } finally {
        clearTimeout(failOpen);
      }
    })();
    return () => { cancelled = true; clearTimeout(failOpen); };
  }, [user?.id, previewEntryKey]);

  // `activation` is the embedded card capture's trial/activate answer
  // ({subscriptionId, status}); the hosted-checkout return and PlanSelectScreen
  // pass nothing.
  async function handlePaid(activation) {
    if (paidPollStarted.current) return;
    paidPollStarted.current = true;
    // GH-1130: Google Ads "Subscribe" for a trial started here — once per
    // Stripe session / subscription id, and only once a read confirms the
    // trial. A hosted-checkout return is usually let in by the
    // checkout.session.completed mirror (accessState 'active') before
    // customer.subscription.created writes the trialing row, so
    // followTrialStart keeps reading in the background after 'allow'.
    // Never awaited: it does not delay the gate. Fail-silent.
    const search = location.search;
    const reportTrialStart = (first) => {
      try {
        followTrialStart({
          search,
          activation,
          first,
          read: () => {
            // Only the entitlements entry: clearing the whole API cache
            // would also make every mounted screen reload its reads.
            api.invalidateCache(['/api/billing/entitlements']);
            return api.billing.getEntitlements();
          },
          isCancelled: () => trialFollowStopped.current,
        }).catch(() => {});
      } catch { /* fail-silent */ }
    };
    // Funnel (TASK-122): handlePaid only runs because a checkout genuinely
    // finished — either the hosted-checkout return URL (?checkout=success /
    // session_id) or the card-gate's onComplete callback. The ref guard
    // above means this fires once per gate mount; a reload with the same
    // success URL still in the address bar could refire it on a fresh
    // mount, a known/accepted limitation of doing this client-side only.
    try { track('checkout_completed'); } catch { /* fail-silent */ }
    try { trackOnce('card_added', { source: getFunnelSource() }); } catch { /* fail-silent */ }
    setDecision('finalizing');
    // The trialing subscription exists in Stripe; our billing_status updates via
    // webhook (a couple of seconds). Poll, then proceed optimistically — they
    // paid, so never trap them waiting.
    for (let i = 0; i < 8; i += 1) {
      try {
        api.clearCache();
        const ent = await api.billing.getEntitlements();
        updateEntitlements(ent);
        const ok = cardGateDecision(ent, { previewMode: previewModeEnabled() }) === 'allow';
        // decision FIRST, then a SILENT reload: a loud reloadUser flips the
        // shared auth loading flag, unmounts+remounts this gate, and the remount
        // re-reads ?checkout=success from the hosted-checkout return URL and
        // re-enters handlePaid → reloadUser → remount forever ("Checking your
        // session…" hang). Silent keeps the gate mounted so 'allow' sticks.
        if (ok) { reportTrialStart(ent); setDecision('allow'); await reloadUser({ silent: true }).catch(() => {}); return; }
      } catch { /* keep polling */ }
      await new Promise((r) => setTimeout(r, 1500));
    }
    // Webhook still hadn't landed after ~12s — let them in optimistically (they
    // paid). Surface it so we can spot webhook-latency issues during ramp-up.
    console.warn('[card-gate] billing not active after activation poll; proceeding optimistically');
    reportTrialStart(null); // the activation answer, or a later background read, can still prove the trial
    setDecision('allow'); // before reloadUser — see the loop note above
    await reloadUser({ silent: true }).catch(() => {});
  }

  if (decision === 'pending' || decision === 'finalizing') return <LoadingScreen />;
  if (decision === 'gate') {
    // P1 of GH-1133 (#1150): the onboarding connect step's social OAuth
    // returns to /app/settings/integrations. RequireOnboarding lets that
    // return through; the wall must too, or a card-less account is stopped
    // here mid-onboarding and never sees "Profile ready". Resume onboarding
    // exactly as MainApp would; the wall shows again on the way into /app.
    if (isOnboardingSocialReturn(location, browserStorage())) {
      return <OnboardingSocialResume><LoadingScreen /></OnboardingSocialResume>;
    }
    // GH-912: while new subscriptions are sold through Link (Stripe Managed
    // Payments) the embedded card-capture fallback is never offered, even with
    // the localStorage override set — see hostedTrialCheckoutEnabled.
    const managedPaymentsNewSubscriptions = resolveManagedPaymentsNewSubscriptions({
      catalogFlag: catalogManagedPayments,
      entitlements: gateEntitlements,
    });
    // GH-1133 S5 (§3.12): a granted preview whose board is hidden
    // (preview/hide_ui=1) sees today's wall plus "your drafts are saved".
    // Every other account gets no extra prop — today's wall byte for byte.
    const hiddenPreviewNotice = gateEntitlements?.freePreview?.hidden === true ? { previewContext: { saved: true } } : {};
    return hostedTrialCheckoutEnabled({ managedPaymentsNewSubscriptions })
      ? <PlanSelectScreen onComplete={handlePaid} {...hiddenPreviewNotice} />
      : <TrialCardCaptureContainer onComplete={handlePaid} />;
  }
  // GH-1133 S5 (design §3.9 item 2): the free preview. The app renders inside
  // FreePreviewProvider (board, route allowlist, trial wall at the moment of
  // intent). The 4 s fail-open and the handlePaid poll above are unchanged:
  // a checkout return still polls to 'allow'; ?checkout=cancelled stays here.
  if (decision === 'preview') {
    return (
      <FreePreviewProvider
        entitlements={gateEntitlements}
        onPaid={handlePaid}
        onLeave={(next, ent) => { setGateEntitlements(ent); setDecision(next); }}
      >
        {children}
      </FreePreviewProvider>
    );
  }
  // PreviewResumeHost renders nothing (and sends nothing) unless the preview's
  // trial wall left a resume intent before a successful checkout.
  return (
    <PostTrialOnboardingGate fallback={<LoadingScreen />} resume={<PreviewResumeHost />}>
      {children}
    </PostTrialOnboardingGate>
  );
}

function LegacySettingsRedirect({ target }) {
  const location = useLocation();
  const destination = new URL(target, window.location.origin);
  const incoming = new URLSearchParams(location.search);
  incoming.forEach((value, key) => destination.searchParams.set(key, value));
  return <Navigate to={`${destination.pathname}${destination.search}`} replace />;
}

// Admin gate for the internal ops screens (/app/admin/*). The SERVER is the real
// authority — every /api/admin/* call runs requireAdmin (→ the `admins` DB
// table), the gateway scrubs x-dathent-caller-worker so internal worker
// endpoints can't be reached directly, and forging headers can't grant admin.
// This client guard is defense-in-depth that also HIDES the screens (and their
// route paths) from non-admins so they can't be discovered. isAdmin is the same
// /api/auth/me signal the Sidebar admin link uses. Fail-closed: anyone not
// flagged admin is bounced to the dashboard (no flash of the admin UI).
function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user?.isAdmin !== true) return <Navigate to="/app" replace />;
  return children;
}

// Minimal chrome for the SPA admin/ops tools — deliberately NOT the user-app
// AppShell (no product sidebar/topbar), so these read as part of the admin
// surface, not the main app. A slim bar with the title and a hard-nav back to
// the static /admin panel (the same /admin the Sidebar links to).
function AdminShell({ title, children }) {
  return (
    <div style={{ minHeight: '100dvh', background: 'var(--bg, #0f0f12)', color: 'var(--text, #e9e9ee)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', borderBottom: '1px solid var(--border, #2a2a30)' }}>
        <a href="/admin" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-muted, #9a9aa2)', fontSize: 13, textDecoration: 'none' }}>← Admin</a>
        <span style={{ fontWeight: 600, fontSize: 15 }}>{title}</span>
        <span style={{ marginLeft: 'auto', fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 10.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-subtle, #6a6a72)' }}>Internal · ops</span>
      </div>
      <div style={{ padding: '22px 18px 56px' }}>{children}</div>
    </div>
  );
}

// Onboarding gate for the whole /app subtree.
//
// The new /app routes (the index Dashboard + every ported screen) render
// their components directly via React Router and NEVER mount the legacy
// <MainApp>, where the original onboarding gate lived. After "/" was changed
// to redirect to "/app", that gate was orphaned — a user with no brand
// profile (onboarding not finished) lands straight on the dashboard and the
// wizard is skipped entirely. This guard restores the gate for all /app
// routes from a single place.
//
// Hard rules, learned from the guard that previously bricked the app:
//   * Fail OPEN. ONLY a clean 404 ("no brand profile") redirects to
//     onboarding. Network errors, 5xx, auth blips, or a slow/stalled check
//     all let the user straight through — we never trap anyone out of the app.
//   * The only local signal is the explicit, account-scoped Skip marker. The
//     server profile remains authoritative and clears that marker when found.
//   * Reviewers on the REVIEW_BYPASS_EMAILS allowlist (Meta/Threads/Instagram
//     app review) carry user.bypassOnboarding=true and are always let through.
// The in-memory "just completed" timestamp lives in lib/onboardingCompletion.js
// (TASK-214): the dashboard's "Finish setup" card honours the same grace window.

function RequireOnboarding({ children }) {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const location = useLocation();
  const [decision, setDecision] = useState('pending');
  // ChatGPT-skip-onboarding (owner decision 2026-09-10): server signupSource
  // (A2, once live) OR the live pendingOAuth hand-off — same isChatGptOrigin()
  // every other consumer (OnboardingNudge, PlanSelectScreen,
  // TrialCardCaptureContainer) reads, EXCEPT the localStorage fallback is
  // disabled (storage=null) here specifically: this is a routing GATE, and
  // `dathent.signup.source` is a browser-wide marker (round-2 review, MEDIUM)
  // — on a shared browser, user B would inherit user A's ChatGPT-plugin
  // marker and skip onboarding. Only the server field or a live pendingOAuth
  // hand-off may skip the interview.
  const pendingOAuth = usePendingOAuth();
  const chatgptOrigin = isChatGptOrigin(pendingOAuth.pending, user, null);

  // The social-connect OAuth round-trip returns to /app/settings/integrations
  // carrying these params; MainApp resumes onboarding from there, so we must
  // NOT intercept it (intercepting strips the params and breaks the resume).
  const params = new URLSearchParams(location.search);
  const isOAuthReturn = params.has('provider') || params.has('connected') || params.has('error');

  useEffect(() => {
    if (
      user?.bypassOnboarding === true ||           // platform-review accounts
      isOAuthReturn ||                             // social-connect resume
      onboardingJustCompleted()                    // just finished; profile may lag on read replicas
    ) {
      setDecision('allow');
      return;
    }
    let cancelled = false;
    // Safety net: if the check stalls, let the user in rather than trap them.
    const failOpen = setTimeout(() => { if (!cancelled) setDecision('allow'); }, 4000);
    (async () => {
      try {
        await api.profile.getBrandProfile();
        if (!cancelled) {
          clearOnboardingSkipped(user?.id);
          setDecision('allow');
        }
      } catch (err) {
        if (cancelled) return;
        // Brand profile is created at onboarding completion. Posting keeps that
        // row as an invisible scoping workspace, so a clean 404 first creates
        // the canonical minimal profile in place; every other plan preserves
        // the existing skip/onboarding decision. TASK (2026-09-11, bare-brand
        // prompts #4): the gateway's server-side e-mail gate must fail CLOSED,
        // not "allow" — behind that gate this call 403s
        // (EMAIL_NOT_VERIFIED) for every account, so the old blanket "anything
        // else fails open" let users into an app that could never load a
        // brand, and a posting account's auto-workspace (only created on a
        // genuine 404, below) never ran. Every OTHER error still fails open.
        const classification = classifyBrandProfileError(err);
        if (classification === 'email_gate') {
          setDecision('email_gate');
        } else if (classification === 'missing') {
          const skipped = onboardingWasSkipped(user?.id);
          let entitlements = null;
          try { entitlements = await api.billing.getEntitlements(); } catch { /* preserve onboarding-first fallback */ }
          const nextDecision = await resolveMissingBrand({
            apiClient: api,
            user,
            language: i18n.resolvedLanguage || i18n.language || 'en',
            skipped,
            entitlements,
            chatgptOrigin,
            onCreated: async () => {
              api.invalidateCache?.(['/api/brand-profile', '/api/profile/brands', '/api/onboarding/status', '/api/strategy']);
              clearOnboardingSkipped(user?.id);
              markOnboardingCompleted();
              try { track('quick_brand_created', { source: 'posting_auto' }); } catch { /* fail-silent */ }
            },
          });
          if (!cancelled) setDecision(nextDecision);
        } else {
          setDecision('allow');
        }
      } finally {
        clearTimeout(failOpen);
      }
    })();
    return () => { cancelled = true; clearTimeout(failOpen); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.bypassOnboarding, user?.signupSource, isOAuthReturn, i18n.language, i18n.resolvedLanguage, pendingOAuth.pending]);

  if (decision === 'pending') return <LoadingScreen />;
  // Fail-closed fallback for the gateway's server-side e-mail gate (criterion
  // 4) — independent of emailVerificationWallEnabled()/RequireVerifiedEmail
  // above, which only reads the client's own user.emailVerified flag and can
  // miss a case the server still enforces.
  if (decision === 'email_gate') return <VerifyEmailWall />;
  if (decision.startsWith('/')) return <Navigate to={decision} replace state={{ from: location }} />;
  return children;
}

function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.querySelector('[data-main-scroll]')?.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    });
  }, [location.pathname, location.search]);

  return null;
}

export function PostingRouteOutlet() {
  const location = useLocation();
  const dashboard = useDashboardData();
  const freePreview = useFreePreview();
  const possibleLock = postingFeatureForPath(location.pathname);
  const readEntitlements = dashboard.entitlements || dashboard.readEntitlements;

  useEffect(() => {
    if (possibleLock && !readEntitlements) dashboard.ensureDashboardData?.({ keys: ['entitlements'] });
  }, [possibleLock, readEntitlements, dashboard.ensureDashboardData]);

  // GH-1133 S5 (§2.4): the free preview has its own route allowlist.
  if (freePreview.active) return <PreviewRouteOutlet />;
  if (possibleLock && !readEntitlements) return <LoadingScreen />;
  // Also enforces the Free publishing plan/strategy boundary outside Preview.
  const lockedSection = postingLockedSection({ pathname: location.pathname, entitlements: readEntitlements });
  if (lockedSection) return <AppShell><PostingFeatureLock section={lockedSection} /></AppShell>;
  return <Outlet key={dashboard.accountId} />;
}

function MainApp({ chromeless = false }) {
  const { user, logout } = useAuth();
  const { hydrateContentItem, activeBatches, content } = useContentFeed();
  const location = useLocation();
  const routerNavigate = useNavigate();
  const [screen, setScreen] = useState(() => {
    const routedScreen = screenFromLocation(window.location);
    if (routedScreen) return routedScreen;
    return 'dashboard';
  });
  const [selectedContent, setSelectedContent] = useState(null);
  const [editorBackScreen, setEditorBackScreen] = useState('content');
  const [editorBackOptions, setEditorBackOptions] = useState(null);
  const [editorBackLabel, setEditorBackLabel] = useState('Back');
  const [contentRouteLoading, setContentRouteLoading] = useState(false);
  const [contentRouteError, setContentRouteError] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('sidebarState') === 'collapsed');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);
  const [assistantNotificationContext, setAssistantNotificationContext] = useState(null);

  // Density default — theme is controlled by ThemeToggle (localStorage 'dathent-theme').
  // Brand colors come from tokens.css; do not override here.
  useEffect(() => {
    document.documentElement.setAttribute('data-density', 'compact');
  }, []);

  useEffect(() => {
    const path = location.pathname.replace(/^\/app(?=\/|$)/, '') || '/';
    const pathSegments = path.replace(/^\/+/, '').split('/');
    const firstSegment = pathSegments[0];
    if (path.startsWith('/settings/integrations')) {
      // Preserve OAuth result params (provider, connected, error, accounts, ...)
      // so the Settings screen can show success/failure feedback.
      const params = new URLSearchParams(location.search || '');
      // TASK-329 round 4 fix (guard contract, 2nd review pass, BLOCKER):
      // the auth-worker's overage guard 302s a blocked (missing
      // overage_ack) connect attempt back to this same return path with
      // ?overage=<platform> — regardless of which flow started the
      // connect. A stale ONBOARDING_SOCIAL_RETURN_KEY / ACTIVATION_RETURN_KEY
      // / ASSISTANT_SOCIAL_RETURN_KEY flag left over from an earlier,
      // unrelated connect made this effect bail out below so the OTHER
      // effect (its own onboarding/activation/assistant branches, further
      // down this file) could route the user away instead — dropping the
      // overage param entirely and landing them back in onboarding/
      // activation/the assistant with no confirm sheet at all. `overage`
      // always wins: clear every stale return flag and go straight to
      // Settings' Accounts tab, whose own deep-link handler
      // (searchParams.get('overage')) reopens <SocialConnectConfirm> from
      // there.
      if (params.has('overage')) {
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(ONBOARDING_SOCIAL_RETURN_KEY);
          window.localStorage.removeItem(ACTIVATION_RETURN_KEY);
          window.localStorage.removeItem(ASSISTANT_SOCIAL_RETURN_KEY);
        }
      } else if (typeof window !== 'undefined' && (window.localStorage.getItem(ONBOARDING_SOCIAL_RETURN_KEY) || window.localStorage.getItem(ACTIVATION_RETURN_KEY) || window.localStorage.getItem(ASSISTANT_SOCIAL_RETURN_KEY))) {
        return;
      }
      params.set('tab', 'accounts');
      routerNavigate(`/app/settings?${params.toString()}`, { replace: true });
      return;
    }
    const legacySettingsTab = SETTINGS_TAB_FOR_LEGACY_SCREEN[firstSegment];
    if (legacySettingsTab) {
      routerNavigate(`/app/settings?tab=${legacySettingsTab}`, { replace: true });
      return;
    }
    // Map legacy /posts/<x> segments under /app to flat /app/<x>
    if (firstSegment === 'posts' && pathSegments[1]) {
      const sub = pathSegments[1];
      const flatMap = { create: 'create', library: 'library', calendar: 'calendar' };
      if (flatMap[sub]) {
        const search = location.search ? location.search : '';
        routerNavigate(`/app/${flatMap[sub]}${search}`, { replace: true });
        return;
      }
    }
    if (firstSegment === 'brand') {
      routerNavigate(`/app/plan${location.search || ''}`, { replace: true });
    }
  }, [location.pathname]);

  useEffect(() => {
    const routedScreen = screenFromLocation(location);
    if (routedScreen) {
      setScreen(routedScreen);
      return;
    }

    // Review-bypass shortcut for Meta/Threads/Instagram test accounts:
    // the auth-worker sets user.bypassOnboarding=true for emails on the
    // REVIEW_BYPASS_EMAILS allowlist. Skip onboarding entirely so app
    // reviewers don't get stuck on the wizard during platform review.
    if (user?.bypassOnboarding === true) {
      setScreen('dashboard');
      return;
    }

    let cancelled = false;
    async function resolveInitialScreen() {
      try {
        const [statusResult, profileResult] = await Promise.allSettled([
          api.onboarding.getStatus(),
          api.profile.getBrandProfile(),
        ]);
        if (cancelled) return;
        const status = statusResult.status === 'fulfilled' ? statusResult.value : null;
        // Sync the UI to the brand's language (e.g. UA brand -> UA app) unless
        // the user explicitly picked a language. No-op on an explicit choice or
        // an unrecognized brand language. Never blocks screen resolution.
        if (profileResult.status === 'fulfilled') {
          clearOnboardingSkipped(user?.id);
          const v = profileResult.value;
          const bp = (v?.data ?? v)?.brandProfile || v?.brandProfile || null;
          if (bp?.language) applyBrandLanguage(bp.language);
        }
        const skipped = onboardingWasSkipped(user?.id);
        const profileMissing = profileResult.status === 'rejected' && profileResult.reason?.status === 404;
        if (!skipped && (status?.onboardingSession?.status === 'in_progress' || profileMissing)) {
          setScreen('onboarding');
          return;
        }
        setScreen('dashboard');
      } catch {
        if (!cancelled) setScreen('dashboard');
      }
    }
    resolveInitialScreen();
    return () => {
      cancelled = true;
    };
  }, [location, user?.id, user?.bypassOnboarding]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(location.search);
    const normalizedPath = location.pathname.replace(/^\/app(?=\/|$)/, '') || '/';
    const hasOAuthResult = params.has('provider') || params.has('connected') || params.has('error');
    const isIntegrationsReturn = normalizedPath.startsWith('/settings/integrations');
    // TASK-329 round 4 fix (guard contract, 2nd review pass, BLOCKER):
    // `overage` always wins over a stale onboarding/activation/assistant
    // return flag — mirrors the matching fix in the effect above (same
    // ?overage=<platform> guard-redirect case). This branch does not gate on
    // hasOAuthResult (the guard-redirect never carries `provider`/
    // `connected`/`error`), and it must run BEFORE the onboarding/assistant/
    // activation branches below: those only check their own flag, so
    // without this they would still fire on a stale flag and route the user
    // away from Settings before ever showing the confirm sheet, and their
    // own routerNavigate call (running after the effect above's in the same
    // commit) would silently override that earlier redirect.
    if (isIntegrationsReturn && params.has('overage')) {
      window.localStorage.removeItem(ONBOARDING_SOCIAL_RETURN_KEY);
      window.localStorage.removeItem(ACTIVATION_RETURN_KEY);
      window.localStorage.removeItem(ASSISTANT_SOCIAL_RETURN_KEY);
      const returnParams = new URLSearchParams(params);
      returnParams.set('tab', 'accounts');
      routerNavigate(`/app/settings?${returnParams.toString()}`, { replace: true });
      return;
    }
    const onboardingReturn = window.localStorage.getItem(ONBOARDING_SOCIAL_RETURN_KEY);
    if (onboardingReturn && isIntegrationsReturn && hasOAuthResult) {
      window.localStorage.removeItem(ONBOARDING_SOCIAL_RETURN_KEY);
      // The forwarded params (and why each one rides along) live in
      // lib/onboardingSocialReturn.js, shared with the card gate's passthrough
      // for card-less accounts (P1 of GH-1133), so the two cannot drift.
      setScreen('onboarding');
      routerNavigate(onboardingSocialResumePath(location.search), { replace: true });
      return;
    }

    const assistantReturn = window.localStorage.getItem(ASSISTANT_SOCIAL_RETURN_KEY);
    if (assistantReturn && isIntegrationsReturn && hasOAuthResult) {
      window.localStorage.removeItem(ASSISTANT_SOCIAL_RETURN_KEY);
      const returnParams = new URLSearchParams({ tab: 'social', assistant: '1' });
      ['provider', 'platform', 'connected', 'accounts', 'error', 'pages', 'instagramBusinessAccounts'].forEach(key => {
        const value = params.get(key);
        if (value) returnParams.set(key, value);
      });
      setScreen('settings');
      setChatOpen(true);
      routerNavigate(`/settings?${returnParams.toString()}`, { replace: true });
      return;
    }

    const activationReturn = window.localStorage.getItem(ACTIVATION_RETURN_KEY);
    if (!activationReturn || !isIntegrationsReturn || !hasOAuthResult) return;
    window.localStorage.removeItem(ACTIVATION_RETURN_KEY);
    setScreen('preview');
    routerNavigate(SCREEN_PATHS.preview, { replace: true });
  }, [location]);

  useEffect(() => {
    const routeContentId = contentIdFromLocation(location);
    if (!routeContentId) {
      setContentRouteError(null);
      setContentRouteLoading(false);
      return;
    }
    if (selectedContent?.contentItem?.id === routeContentId) {
      setContentRouteError(null);
      setContentRouteLoading(false);
      hydrateContentItem(routeContentId)
        .then(content => {
          if (content) setSelectedContent(current => current?.contentItem?.id === routeContentId ? content : current);
        })
        .catch(() => {});
      return;
    }
    let cancelled = false;
    async function loadRouteContent() {
      setContentRouteLoading(true);
      setContentRouteError(null);
      try {
        const content = await hydrateContentItem(routeContentId);
        if (!cancelled) setSelectedContent(content);
      } catch (err) {
        if (!cancelled) setContentRouteError(err.message || 'Unable to load content item');
      } finally {
        if (!cancelled) setContentRouteLoading(false);
      }
    }
    loadRouteContent();
    return () => {
      cancelled = true;
    };
  }, [location]);

  useEffect(() => {
    if (screen !== 'preview' || contentIdFromLocation(location) || selectedContent) return;
    const raw = sessionStorage.getItem('selected_content');
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      if (parsed?.contentItem?.id) setSelectedContent(parsed);
    } catch {
      sessionStorage.removeItem('selected_content');
    }
  }, [screen, location, selectedContent]);

  useEffect(() => {
    let cancelled = false;
    async function loadUnreadCount() {
      try {
        const response = await api.notifications.getUnreadCount();
        if (!cancelled) {
          setNotificationUnreadCount(Number(response.unreadCount || 0));
        }
      } catch {
        if (!cancelled) {
          setNotificationUnreadCount(0);
        }
      }
    }
    loadUnreadCount();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!mobileSidebarOpen) return undefined;
    const scrollY = window.scrollY;
    const previousOverflow = document.body.style.overflow;
    const previousPosition = document.body.style.position;
    const previousTop = document.body.style.top;
    const previousWidth = document.body.style.width;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.position = previousPosition;
      document.body.style.top = previousTop;
      document.body.style.width = previousWidth;
      window.scrollTo(0, scrollY);
    };
  }, [mobileSidebarOpen]);

  const openContentById = (contentId, options = {}) => {
    const normalizedContentId = String(contentId || '').trim();
    if (!normalizedContentId) return false;
    const existing = content.find(item => item.id === normalizedContentId || item.raw?.contentItem?.id === normalizedContentId);
    setEditorBackScreen(options.backScreen || screen || 'content');
    setEditorBackOptions(options.backOptions || null);
    setEditorBackLabel(options.backLabel || 'Back');
    setSelectedContent(existing?.raw || null);
    setContentRouteError(null);
    setContentRouteLoading(!existing?.raw);
    setScreen('preview');
    setMobileSidebarOpen(false);
    setNotificationsOpen(false);
    if (options.keepAssistantOpen) setChatOpen(true);
    routerNavigate(`/content/${encodeURIComponent(normalizedContentId)}/preview`, options.replace ? { replace: true } : undefined);
    return true;
  };

  const navigate = (id, options = {}) => {
    // fix #4 (judge review): a caller can pass a real router PATH here (e.g.
    // AIAssistant's locked-state "View plans" button calls
    // navigateFromResult('/app/plans')) rather than a legacy screen id. Before
    // this fix, a leading-slash string fell through to `setScreen(id)` below,
    // which treats ANY string as a screen id — `setScreen('/app/plans')`
    // matches no known screen and renders a blank page. Recognize a path
    // up front and hand it straight to the router, the same way the
    // onboarding-completion path already does for
    // '/posts/create?source=onboarding'.
    if (typeof id === 'string' && id.startsWith('/')) {
      setMobileSidebarOpen(false);
      setNotificationsOpen(false);
      routerNavigate(id, options.replace ? { replace: true } : undefined);
      return;
    }
    if (id === 'preview' && options.contentId) {
      openContentById(options.contentId, options);
      return;
    }
    if (SETTINGS_TAB_FOR_LEGACY_SCREEN[id]) {
      setScreen('settings');
      setMobileSidebarOpen(false);
      setNotificationsOpen(false);
      routerNavigate(`/settings?tab=${SETTINGS_TAB_FOR_LEGACY_SCREEN[id]}`, options.replace ? { replace: true } : undefined);
      return;
    }
    if (id === 'content') {
      setSelectedContent(null);
      sessionStorage.removeItem('selected_content');
    }
    setScreen(id);
    setMobileSidebarOpen(false);
    setNotificationsOpen(false);
    if (SCREEN_PATHS[id]) {
      const path = SCREEN_PATHS[id];
      const params = new URLSearchParams();
      if (options.focusPostId) params.set('focus', options.focusPostId);
      if (options.batchId) params.set('batch', options.batchId);
      if (options.filter) params.set('filter', options.filter);
      const query = params.toString();
      routerNavigate(query ? `${path}?${query}` : path, options.replace ? { replace: true } : undefined);
    }
  };

  const setDesktopSidebarCollapsed = (collapsed) => {
    setSidebarCollapsed(collapsed);
    localStorage.setItem('sidebarState', collapsed ? 'collapsed' : 'expanded');
  };

  const openMobileSidebar = () => {
    setSidebarCollapsed(false);
    setMobileSidebarOpen(true);
  };

  const completeOnboarding = (destination = 'dashboard', { onboardingCompleted = true } = {}) => {
    // Onboarding just wrote the brand profile. Mark the time so the /app
    // RequireOnboarding gate lets the user through for the next minute even
    // if the fresh profile hasn't propagated to a D1 read replica yet —
    // otherwise it could bounce them straight back to /onboarding.
    if (onboardingCompleted) {
      clearOnboardingSkipped(user?.id);
      markOnboardingCompleted();
    }
    if (destination === 'firstPostPreview') {
      setScreen('preview');
      routerNavigate(SCREEN_PATHS.preview, { replace: true });
      return;
    }
    if (destination === 'createFirstPost') {
      setScreen('create');
      routerNavigate('/posts/create?source=onboarding', { replace: true });
      return;
    }
    // 'dashboard?tour=1' — onboarding-celebrate's "Show me around" path.
    // We strip the query off the key but pass it through in the navigate
    // so DashboardTour picks it up on mount.
    if (destination === 'dashboard?preview=1') {
      setScreen('dashboard');
      routerNavigate('/app?preview=1', { replace: true });
      return;
    }
    if (destination === 'dashboard?tour=1') {
      setScreen('dashboard');
      routerNavigate('/app?tour=1', { replace: true });
      return;
    }
    // 'post:<id>' — onboarding-activation's first-post landing. Take the
    // user straight to the freshly-generated post so they can review
    // and publish. AppShell routes /app/post/:id to the new PostPreview.
    if (typeof destination === 'string' && destination.startsWith('post:')) {
      const postId = destination.slice('post:'.length);
      if (postId) {
        setScreen('preview');
        routerNavigate(`/app/post/${encodeURIComponent(postId)}`, { replace: true });
        return;
      }
    }
    // 'billing' — user clicked "Choose a plan" on the first-post-ready
    // upsell. Land them on the plan picker.
    if (destination === 'billing') {
      setScreen('settings');
      routerNavigate('/app/settings?tab=billing', { replace: true });
      return;
    }
    const target = destination === 'brand' || destination === 'brandReview' ? 'brand' : 'dashboard';
    setScreen(target);
    routerNavigate(destination === 'brandReview' ? '/brand?review=onboarding' : SCREEN_PATHS[target], { replace: true });
  };

  const signOut = async () => {
    await logout();
    routerNavigate('/login', { replace: true });
  };

  const openContent = (content, options = {}) => {
    setEditorBackScreen(options.backScreen || screen);
    setEditorBackOptions(options.backOptions || null);
    setEditorBackLabel(options.backLabel || 'Back');
    setSelectedContent(content);
    setScreen('preview');
    const contentId = content?.contentItem?.id;
    routerNavigate(contentId ? `/content/${encodeURIComponent(contentId)}/preview` : SCREEN_PATHS.preview);
  };

  const handleContentGenerated = (content) => {
    setSelectedContent(content);
    openContent(content);
  };

  const editorContentView = (component) => {
    if (contentRouteLoading) {
      return <ContentRouteState icon="refresh" title="Loading content" body="Opening content from the shared link..." />;
    }
    if (contentRouteError) {
      return <ContentRouteState icon="alert" title="Content not available" body={contentRouteError} action={() => navigate('content')} />;
    }
    return component;
  };

  // Global listeners (assistant-open event + Cmd/Ctrl-K). MUST stay above the
  // screen early-returns below: if a hook is declared after `if (screen ===
  // 'onboarding') return`, then flipping `screen` to 'onboarding' post-mount
  // (e.g. the social-connect OAuth round-trip that redirects /app/settings/
  // integrations -> /onboarding) renders fewer hooks and React throws
  // "rendered fewer hooks than expected" (#300). Keeping them here makes the
  // hook count constant across every screen.
  useEffect(() => {
    if (!chromeless) return;
    const onOpen = () => setChatOpen(true);
    window.addEventListener('dathent:assistant:open', onOpen);
    return () => window.removeEventListener('dathent:assistant:open', onOpen);
  }, [chromeless]);

  useEffect(() => {
    if (chromeless) return undefined;
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && (event.key === 'k' || event.key === 'K')) {
        event.preventDefault();
        setChatOpen(open => !open);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [chromeless]);

  if (screen === 'onboarding') {
    let onboardingContent = null;
    if (mcpOnboardingEnabled() && new URLSearchParams(location.search).get('source') !== 'trial-setup') {
      if (location.pathname === '/onboarding/connect') onboardingContent = <OnboardingConnect />;
      else if (location.pathname === '/onboarding/say-hello') onboardingContent = <OnboardingSayHello />;
      const onboardingChoice = readMcpOnboardingChoice();
      if (!onboardingContent && !onboardingChoice) onboardingContent = <OnboardingFork />;
      if (!onboardingContent && onboardingChoice === 'ai') onboardingContent = <OnboardingConnect />;
    }
    return <FreePublishingOnboardingGate>{onboardingContent || <Onboarding onComplete={completeOnboarding}/>}</FreePublishingOnboardingGate>;
  }

  const titles = {
    dashboard: { t: 'Autopilot', s: 'Status of your AI content engine' },
    scheduler: { t: 'Calendar', s: 'Calendar and queue of upcoming publications' },
    content: { t: 'Library', s: 'All your generated content in one place' },
    create: { t: 'Create post', s: 'Generate content with AI' },
    preview: { t: 'Post preview', s: 'Review post status and next actions' },
    carousel: { t: 'Carousel editor', s: 'Edit your carousel content' },
    editor: { t: 'Edit post', s: 'Refine your post before scheduling' },
    social: { t: 'Social accounts', s: 'Connect and manage your social media accounts' },
    brand: { t: 'Content Plan', s: 'Strategy, brand identity, and per-platform plans' },
    billing: { t: 'Plan & Usage', s: 'Manage plan, usage limits, and subscription' },
    help: { t: 'Help Center', s: 'Answers, setup guides, and support' },
    settings: { t: 'Settings', s: 'Manage your account and preferences' },
    notFound: { t: 'Page not found', s: 'This route does not exist' },
  };

  const showTopbar = screen !== 'notFound';
  const isEditorScreen = screen === 'editor' || screen === 'carousel';
  const assistantSelectedEntity = selectedEntityForAssistant(screen, selectedContent);
  const openAssistantFromNotification = (context) => {
    setAssistantNotificationContext({ ...context, requestId: Date.now() });
    setChatOpen(true);
  };

  return (
    <div className={chromeless ? 'app-legacy-content' : ('app app-shell cosmic-bg ' + (sidebarCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'))} data-sidebar={sidebarCollapsed ? 'icons' : 'wide'} data-chat={chatOpen ? 'open' : 'closed'} data-mobile-sidebar={mobileSidebarOpen ? 'open' : 'closed'} data-screen={screen}>
      {!chromeless && (
        <>
          <Sidebar current={screen} onNavigate={navigate} collapsed={sidebarCollapsed} onToggle={() => setDesktopSidebarCollapsed(!sidebarCollapsed)} onExpand={() => setDesktopSidebarCollapsed(false)} user={user} onLogout={signOut} mobileOpen={mobileSidebarOpen} onMobileClose={() => setMobileSidebarOpen(false)}/>
          <div className="sidebar-scrim" onClick={() => setMobileSidebarOpen(false)} />
        </>
      )}
      <main className={chromeless ? '' : 'main'} data-main-scroll>
        {!chromeless && showTopbar && (
          <Topbar
            title={titles[screen]?.t}
            subtitle={isEditorScreen ? null : titles[screen]?.s}
            onMobileMenu={openMobileSidebar}
            navigationMode={['preview', 'editor', 'carousel'].includes(screen) ? 'back' : 'menu'}
            onBack={() => navigate(editorBackScreen || 'content', editorBackOptions || {})}
            onLogoClick={() => navigate('dashboard')}
            actions={
              <>
                {screen === 'content' && <button className="btn btn-primary btn-sm mobile-header-primary" onClick={() => navigate('create')}><Icon name="plus" size={14}/><span>Create</span></button>}
                {!isEditorScreen && screen !== 'content' && <button className="btn btn-icon btn-ghost"><Icon name="search" size={14}/></button>}
                <button className="btn btn-ai" onClick={() => setChatOpen(true)}>
                  <Icon name="sparkles" size={14}/> Ask AI
                </button>
              </>
            }
          />
        )}
        {/* ActiveGenerationBanner removed. This was the third (legacy)
            surface that emitted "Generating: <topic> · 2/2 ready · Resume
            preview · Open Library" as a stretched banner at the top of
            every non-editor screen during a batch. The user reported
            seeing it on /app/help alongside the new GenerationStatusPlate
            — duplicate info, conflicting layouts.
            The new GenerationStatusPlate (mounted globally in AppShell)
            already surfaces in-progress batches with a richer floating
            card AND auto-navigates to /app/batch/:id when the user clicks
            it. Banner was redundant. The ActiveGenerationBanner component
            below remains defined for now in case another caller needs
            it — but no longer mounted. */}
        {!showTopbar && (
          <button className="mobile-menu-trigger mobile-menu-floating" onClick={openMobileSidebar} aria-label="Open navigation">
            <Icon name="menu" size={18}/>
          </button>
        )}
        {screen === 'dashboard' && <Dashboard navigate={navigate}/>} 
        {screen === 'scheduler' && <Scheduler navigate={navigate} openPost={openContent}/>} 
        {screen === 'content' && <Content navigate={navigate} openPost={openContent}/>} 
        {screen === 'create' && <Create navigate={navigate} openContent={openContent} onContentGenerated={handleContentGenerated}/>} 
        {screen === 'preview' && editorContentView(<LegacyPostPreview navigate={navigate} content={selectedContent} onContentUpdated={setSelectedContent} backTo={editorBackScreen} backOptions={editorBackOptions} backLabel={editorBackLabel}/>)}
        {screen === 'carousel' && editorContentView(<CarouselEditor navigate={navigate} content={selectedContent} onContentUpdated={setSelectedContent} backTo={editorBackScreen} backOptions={editorBackOptions}/>)} 
        {screen === 'editor' && editorContentView(<PostEditor navigate={navigate} content={selectedContent} onContentUpdated={setSelectedContent} backTo={editorBackScreen} backOptions={editorBackOptions} backLabel={editorBackLabel}/>)} 
        {screen === 'brand' && <ContentPlanScreen onAskAI={() => setChatOpen(true)}/>}
        {screen === 'help' && <HelpCenter/>}
        {screen === 'settings' && <Settings/>}
        {screen === 'notFound' && <NotFound onNavigate={navigate}/>} 
      </main>

      {notificationsOpen && (
        <>
          <div className="notif-scrim" onClick={() => setNotificationsOpen(false)} />
          <NotificationsPanel onClose={() => setNotificationsOpen(false)} onNavigate={navigate} onUnreadChange={setNotificationUnreadCount} onOpenAssistant={openAssistantFromNotification}/>
        </>
      )}

      {/* Floating Assistant FAB + panel — only when MainApp is NOT wrapped
          by the new AppShell (which provides its own FAB + GlobalAssistant). */}
      {!chromeless && (
        <>
          <button
            className={'as-fab ' + (chatOpen ? 'is-open' : '')}
            onClick={() => setChatOpen(open => !open)}
            aria-label={chatOpen ? 'Close assistant' : 'Open assistant'}
            title="Dathent Assistant (Ctrl+K)"
          >
            <span className="as-fab-ring"/>
            <span className="as-fab-ring as-fab-ring-2"/>
            <span className="as-fab-ic">
              {chatOpen ? <Icon name="x" size={20}/> : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <defs>
                    <linearGradient id="dathentFabSparkle" x1="0" x2="1" y1="0" y2="1">
                      <stop offset="0%" stopColor="#fff" stopOpacity="1"/>
                      <stop offset="100%" stopColor="#fff" stopOpacity="0.85"/>
                    </linearGradient>
                  </defs>
                  <path d="M12 3 13.5 8 18 9.5 13.5 11 12 16 10.5 11 6 9.5 10.5 8z" fill="url(#dathentFabSparkle)"/>
                  <path d="M19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8z" fill="#fff" opacity="0.9"/>
                </svg>
              )}
            </span>
            {!chatOpen && <span className="as-fab-badge"/>}
          </button>

          <AIPanel
            open={chatOpen}
            onClose={() => setChatOpen(false)}
            currentScreen={screen}
            selectedEntity={assistantSelectedEntity}
            notificationContext={assistantNotificationContext}
            onVoice={() => setVoiceOpen(true)}
            navigate={navigate}
          />

          {voiceOpen && <VoiceMode onClose={() => setVoiceOpen(false)}/>}
        </>
      )}
    </div>
  );
}

function selectedEntityForAssistant(screen, selectedContent) {
  if (['preview', 'editor', 'carousel'].includes(screen)) {
    return {
      type: 'contentItem',
      id: selectedContent?.contentItem?.id || selectedContent?.id || null,
    };
  }
  if (screen === 'brand') return { type: 'brandProfile', id: null };
  if (screen === 'billing') return { type: 'billingAccount', id: null };
  return null;
}

const ACTIVE_GENERATION_STATUSES = new Set(['queued', 'generating', 'reserved']);

function ActiveGenerationBanner({ batches = [], content = [], currentScreen, location, onResume, onOpenLibrary }) {
  const currentBatchId = new URLSearchParams(location.search).get('batch');
  const visibleBatches = (batches || [])
    .filter(isActiveGenerationBatch)
    .filter(batch => !(currentScreen === 'create' && batch.id === currentBatchId))
    .sort((a, b) => Date.parse(b.createdAt || 0) - Date.parse(a.createdAt || 0));

  if (!visibleBatches.length) return null;

  const batch = visibleBatches[0];
  const batchItems = content.filter(item => item.generationBatchId === batch.id);
  const total = Math.max(1, Number(batch.totalItems || 0), batchItems.length);
  const readyCount = Math.max(
    Number(batch.readyCount || 0),
    batchItems.filter(item => ['ready', 'scheduled', 'publishing', 'published'].includes(item.status)).length,
  );
  const failedCount = Math.max(Number(batch.failedCount || 0), batchItems.filter(item => item.status === 'failed').length);
  const progress = generationProgressForBatch(batch, batchItems, total);
  const moreCount = visibleBatches.length - 1;
  const topic = batch.topic || batchItems[0]?.title || 'Generating content';

  return (
    <div className="global-generation-banner" role="status" aria-live="polite">
      <div className="global-generation-orb"><Icon name="sparkles" size={15} /></div>
      <div className="global-generation-main">
        <div className="global-generation-row">
          <div className="global-generation-copy">
            <div className="global-generation-title">Generating: {topic}</div>
            <div className="global-generation-meta">
              {readyCount}/{total} ready
              {failedCount > 0 ? ` · ${failedCount} failed` : ''}
              {moreCount > 0 ? ` · ${moreCount} more running` : ''}
            </div>
          </div>
          <div className="global-generation-actions">
            <button className="btn btn-secondary btn-sm" onClick={() => onResume(batch)}><Icon name="play" size={12} /> Resume preview</button>
            <button className="btn btn-ghost btn-sm" onClick={() => onOpenLibrary(batch)}><Icon name="grid" size={12} /> Open Library</button>
          </div>
        </div>
        <div className="global-generation-progress"><div style={{ width: `${progress}%` }} /></div>
      </div>
    </div>
  );
}

function isActiveGenerationBatch(batch) {
  return batch?.id && ACTIVE_GENERATION_STATUSES.has(batch.status);
}

function generationProgressForBatch(batch, items, total) {
  const batchProgress = Number(batch?.progress);
  const done = (items || []).filter(item => ['ready', 'scheduled', 'publishing', 'published', 'failed'].includes(item.status)).length;
  const rowProgress = Math.round((done / Math.max(1, total)) * 100);
  const progress = Number.isFinite(batchProgress) && batchProgress > 0 ? Math.max(batchProgress, rowProgress) : rowProgress;
  return Math.max(5, Math.min(100, Math.round(progress)));
}

const LIBRARY_FILTER_STATUSES = new Set(['generating', 'ready', 'scheduled', 'published', 'draft', 'failed']);

function libraryFilterForBatch(batch, content) {
  const items = content.filter(item => item.generationBatchId === batch.id);
  const filter = libraryFilterForStatuses(items.map(item => item.status));
  if (filter) return filter;
  if (!items.length && isActiveGenerationBatch(batch)) return 'generating';
  return null;
}

function libraryFilterForStatuses(statuses) {
  const filters = [...new Set(statuses.map(normalizeLibraryFilterStatus).filter(Boolean))];
  return filters.length === 1 ? filters[0] : null;
}

function normalizeLibraryFilterStatus(status) {
  return LIBRARY_FILTER_STATUSES.has(status) ? status : null;
}

function ContentRouteState({ icon, title, body, action }) {
  return (
    <div className="page" style={{ maxWidth: 720 }}>
      <div className="card content-empty-v2">
        <Icon name={icon} size={24} />
        <h2 className="h2" style={{ margin: '12px 0 8px' }}>{title}</h2>
        <p className="body" style={{ color: 'var(--text-3)', marginBottom: action ? 18 : 0 }}>{body}</p>
        {action && <button className="btn btn-primary" onClick={action}>Back to content</button>}
      </div>
    </div>
  );
}

/**
 * Bridges ContentFeed transitions into GenerationStatus completions.
 *
 * Watches the content feed for publish_now ops we're tracking: when a
 * scheduledPost's targets all flip to published (or all to failed), the
 * matching op in GenerationStatusContext is completed/failed so the
 * status plate flips state without each trigger site polling itself.
 */
export function GenerationStatusContentFeedBridge() {
  const { content, activeBatches } = useContentFeedContext();
  const { operations, completeOperation, updateOperation } = useGenerationStatus();
  const lastSnapshotRef = React.useRef(new Map()); // sourceRef.id -> status signature

  // ── Bridge 1: content batch operations ──
  // When a content_batch op is started from AI Assistant / Create / etc,
  // we watch the activeBatches feed for that batch.id and mirror its
  // progress (totalItems / readyCount / failedCount / status) into the
  // op. Terminal status flips → completeOperation. Stays in lockstep
  // with the existing BatchActivityBridge but speaks to the new plate.
  React.useEffect(() => {
    if (!operations) return;
    const batchOps = Object.values(operations).filter((op) =>
      op.kind === 'content_batch' &&
      op.status !== 'succeeded' && op.status !== 'failed' && op.status !== 'cancelled' &&
      op.sourceRef?.type === 'batch'
    );
    if (!batchOps.length) return;

    const batchById = new Map();
    (activeBatches || []).forEach((b) => { if (b?.id) batchById.set(b.id, b); });

    // Per-item visual-still-cooking detector. ContentFeedContext exposes
    // `visualStatus` and `jobFormat` on each content row but does NOT set
    // a precomputed `visualStillCooking` boolean — so the bridge has to
    // derive it inline using the same heuristic PostPreview uses for its
    // in-flight overlay. Without this gate, image_post batches mark the
    // op as succeeded the moment text saves (5-10s) even though the
    // image is still being generated (another 30-60s).
    const visualStillCooking = (c) => {
      if (c.status === 'failed' || c.generationStatus === 'failed') return false;
      const fmt = String(c.jobFormat || c.format || '').toLowerCase();
      if (fmt !== 'image_post') return false;
      const vs = String(c.visualStatus || '').toLowerCase();
      if (['ready', 'succeeded', 'completed', 'failed'].includes(vs)) return false;
      if (c.imageUrl) return false;
      if (Array.isArray(c.imageUrls) && c.imageUrls.length > 0) return false;
      return true;
    };

    batchOps.forEach((op) => {
      const batch = batchById.get(op.sourceRef.id);
      if (!batch) {
        // Batch not in activeBatches snapshot. Two reasons:
        //   (a) freshly created, server hasn't flushed yet → wait
        //   (b) batch row pruned out of active set (terminal status)
        // For (b), the content rows still carry generationBatchId so
        // we can check them directly: if items exist + all done + no
        // visual still cooking, we mark terminal. Otherwise wait.
        const items = (content || []).filter((c) => c.generationBatchId === op.sourceRef.id);
        if (!items.length) return;
        const total = op.total || items.length;
        const allDone = items.length >= total && items.every((c) =>
          ['ready', 'failed', 'scheduled', 'publishing', 'published'].includes(c.status)
          && (!c.generationStatus || ['ready', 'failed'].includes(c.generationStatus))
          && !visualStillCooking(c)
        );
        if (!allDone) return;
        const ready = items.filter((c) => c.status !== 'failed' && c.generationStatus !== 'failed').length;
        const failed = items.length - ready;
        completeOperation(op.id, {
          status: failed > 0 && ready === 0 ? 'failed' : 'succeeded',
          completed: ready,
          failed,
          ...(failed > 0 && ready === 0 ? batchGenerationFailure(items) : { error: null, errorCode: null }),
        });
        return;
      }
      const total = Math.max(1, Number(batch.totalItems || batch.total_items || op.total || 0));
      const ready = Math.max(0, Number(batch.readyCount || batch.ready_count || 0));
      const failed = Math.max(0, Number(batch.failedCount || batch.failed_count || 0));

      // Per-item terminal check is the AUTHORITATIVE done signal because
      // batch.status flips to 'ready' the moment text drafts save — long
      // before image_post items have their visuals finalized. We require
      // EVERY item to be in a stable status AND past its active
      // generation stage AND not still cooking visuals.
      const items = (content || []).filter((c) => c.generationBatchId === batch.id);
      const itemsAllDone = items.length > 0 && items.length >= total && items.every((c) => {
        if (!['ready', 'failed', 'scheduled', 'publishing', 'published'].includes(c.status)) return false;
        if (c.generationStatus && !['ready', 'failed'].includes(c.generationStatus)) return false;
        if (visualStillCooking(c)) return false;
        return true;
      });

      // Signature includes per-item visual status so we re-tick whenever
      // visuals progress even though `ready/failed/total` from batch row
      // didn't change.
      const itemsSig = items.map((c) => `${c.id}:${c.status}:${c.generationStatus || ''}:${c.visualStatus || ''}:${c.generationErrorCode || ''}:${visualStillCooking(c) ? 'V' : ''}`).sort().join(',');
      const sig = `${ready}|${failed}|${total}|${itemsAllDone ? 'T' : 'A'}|${itemsSig}`;
      if (lastSnapshotRef.current.get(op.sourceRef.id) === sig) return;
      lastSnapshotRef.current.set(op.sourceRef.id, sig);

      if (itemsAllDone) {
        // Jobs/items can settle before the batch rollup updates its counters.
        const ready = items.filter((c) => c.status !== 'failed' && c.generationStatus !== 'failed').length;
        const failed = items.length - ready;
        completeOperation(op.id, {
          status: failed > 0 && ready === 0 ? 'failed' : 'succeeded',
          completed: ready,
          failed,
          ...(failed > 0 && ready === 0 ? batchGenerationFailure(items) : { error: null, errorCode: null }),
        });
      } else {
        // Surface a friendlier progress count that accounts for cooking
        // visuals: an item with text saved but visual cooking shouldn't
        // count as "done" in the user's mental model.
        const visuallyReady = items.filter((c) =>
          ['ready', 'scheduled', 'publishing', 'published'].includes(c.status)
          && (!c.generationStatus || c.generationStatus === 'ready')
          && !visualStillCooking(c)
        ).length;
        updateOperation(op.id, {
          status: 'running',
          completed: visuallyReady,
          failed,
          progress: visuallyReady,
          total,
        });
      }
    });
  }, [activeBatches, content, operations, completeOperation, updateOperation]);

  // ── Bridge 2: publish operations ──
  React.useEffect(() => {
    if (!operations) return;
    const ops = Object.values(operations);
    const publishOps = ops.filter((op) =>
      op.kind === 'publish' &&
      op.status !== 'succeeded' && op.status !== 'failed' && op.status !== 'cancelled' &&
      op.sourceRef?.type === 'scheduledPost'
    );
    if (!publishOps.length && !ops.some((op) => op.sourceRef?.type === 'contentItem' && op.kind === 'publish' && op.status === 'running')) {
      return;
    }

    // Build a map: scheduledPostId -> { targets, contentItemId }
    const targetsByScheduledPost = new Map();
    const summaryByContentItem = new Map();
    content.forEach((item) => {
      const summary = item?.publishSummary;
      if (!summary) return;
      summaryByContentItem.set(item.id, summary);
      if (summary.scheduledPostId) {
        targetsByScheduledPost.set(summary.scheduledPostId, {
          targets: summary.targets || [],
          contentItemId: item.id,
        });
      }
    });

    publishOps.forEach((op) => {
      const info = targetsByScheduledPost.get(op.sourceRef.id);
      if (!info) return;
      const { targets } = info;
      if (!targets.length) return;

      // Only treat targets as "part of this op" if their state was
      // updated AFTER the op started. This is the key fix for retry:
      // on first tick the targets are still in their pre-retry failed
      // state (with old lastErrorMessage), and without this gate the
      // bridge would immediately mark the op as failed using stale
      // data — even though the dispatcher is actively in the middle
      // of retrying. The retry then succeeds but the op is already
      // terminal, so the plate stays red with the wrong error.
      //
      // -2s tolerance for clock skew between client and worker.
      const opStartedMs = op.startedAt || 0;
      const recencyCutoff = opStartedMs - 2000;
      const isRecent = (t) => {
        const ts = Date.parse(t.updatedAt || t.failedAt || t.publishedAt || '') || 0;
        return ts >= recencyCutoff;
      };
      const relevantTargets = targets.filter(isRecent);
      // If NO targets are fresh yet, the op is still in flight (the
      // dispatcher hasn't even claimed it) — keep showing it as running
      // and wait for the next tick.
      if (!relevantTargets.length) {
        const sig = `pending|${targets.length}`;
        if (lastSnapshotRef.current.get(op.sourceRef.id) === sig) return;
        lastSnapshotRef.current.set(op.sourceRef.id, sig);
        return;
      }

      const completed = relevantTargets.filter((t) => t.status === 'published' || t.status === 'needs_manual_action').length;
      const failed = relevantTargets.filter((t) => ['failed', 'needs_reconnect'].includes(t.status)).length;
      const total = relevantTargets.length;
      const platformStatus = {};
      // Build platform status from ALL targets so the user sees existing
      // success on other channels too, but completion math uses only
      // the fresh ones.
      targets.forEach((t) => {
        if (!isRecent(t) && t.status !== 'published') return;
        platformStatus[t.platform] =
          t.status === 'published' || t.status === 'needs_manual_action' ? 'succeeded'
          : ['failed', 'needs_reconnect'].includes(t.status) ? 'failed'
          : 'running';
      });

      // Signature gate: skip if nothing changed.
      const sig = `${completed}|${failed}|${total}|${relevantTargets.map(t => `${t.platform}:${t.status}:${t.updatedAt}`).sort().join(',')}`;
      if (lastSnapshotRef.current.get(op.sourceRef.id) === sig) return;
      lastSnapshotRef.current.set(op.sourceRef.id, sig);

      if (completed + failed >= total) {
        completeOperation(op.id, {
          status: failed === 0 ? 'succeeded' : (completed === 0 ? 'failed' : 'succeeded'),
          completed,
          failed,
          platformStatus,
          // Only surface a recently-updated target's error — not stale
          // lastErrorMessage from a previous failed attempt.
          error: failed > 0 && completed === 0 ? relevantTargets.find((t) => t.lastErrorMessage)?.lastErrorMessage || 'Publish failed' : null,
        });
      } else {
        updateOperation(op.id, {
          status: 'running',
          completed,
          failed,
          progress: completed,
          total,
          platformStatus,
        });
      }
    });

    // contentItem-keyed ops (when scheduledPostId wasn't known at start time):
    // try to upgrade them to scheduledPost-keyed once we see the summary.
    ops
      .filter((op) => op.kind === 'publish' && op.sourceRef?.type === 'contentItem' && (op.status === 'pending' || op.status === 'running'))
      .forEach((op) => {
        const summary = summaryByContentItem.get(op.sourceRef.id);
        if (summary?.scheduledPostId) {
          updateOperation(op.id, {
            sourceRef: { type: 'scheduledPost', id: summary.scheduledPostId },
          });
        }
      });
  }, [content, operations, completeOperation, updateOperation]);

  return null;
}

export default function AppRoot() {
  const { t } = useTranslation();
  return (
    <BrowserRouter>
      <ScrollToTop />
      {/* TASK-053: one upload surface for the whole app — the panel must
          survive navigating between Library / New Post / the post editor,
          which is exactly what "uploads keep running in the background" means. */}
      <UploadProgressProvider>
      <Routes>
        {/* Partner report 2026-09-09: the ChatGPT connector's consent page
            (dathent-mcp-worker renderConsentPage) links "Not you? Use a
            different account" here with ?next= carrying the pending
            authorize URL — signs out, then hands off to /login?next=... so
            the pending authorization resumes once someone else signs in. */}
        <Route path="/logout" element={<Logout />} />
        <Route path="/login" element={<NewAuth mode="login" />} />
        <Route path="/signup" element={<NewAuth mode="signup" />} />
        <Route path="/forgot" element={<NewAuth mode="forgot" />} />
        <Route path="/reset" element={<NewAuth mode="reset" />} />
        <Route path="/confirm" element={<NewAuth mode="confirm" />} />

        {/* Root → new AppShell dashboard. Without this, "/" fell into the
            /* catch-all below and rendered the LEGACY MainApp chrome
            (old sidebar + "Ask AI" topbar). */}
        <Route path="/" element={<Navigate to="/app" replace />} />

        {/* Redirects from legacy paths → /app/* (pack canonical) */}
        <Route path="/dashboard" element={<Navigate to="/app" replace />} />
        <Route path="/posts/create" element={<Navigate to="/app/create" replace />} />
        <Route path="/posts/library" element={<Navigate to="/app/library" replace />} />
        <Route path="/posts/calendar" element={<Navigate to="/app/calendar" replace />} />
        <Route path="/content-plan" element={<Navigate to="/app/plan" replace />} />
        <Route path="/brand" element={<Navigate to="/app/plan" replace />} />
        <Route path="/settings" element={<LegacySettingsRedirect target="/app/settings" />} />
        <Route path="/social" element={<Navigate to="/app/settings?tab=social" replace />} />
        <Route path="/billing" element={<LegacySettingsRedirect target="/app/settings?tab=billing" />} />
        <Route path="/help" element={<Navigate to="/app/help" replace />} />
        <Route path="/preview" element={<Navigate to="/app/preview" replace />} />

        {/* TASK-239 (issue #357, EPIC-027): embedded Shopify app entry point.
            Shopify loads this URL inside an iframe on admin.shopify.com with
            ?shop=...&host=... query params. There's no embedded-specific UI
            yet, so just forward into the normal app shell — LegacySettingsRedirect
            already preserves every query param (shop/host included) so App
            Bridge can read them once it's wired up. public/_headers carries
            the matching frame-ancestors CSP for /shopify/* so the iframe isn't
            blocked by the global X-Frame-Options: DENY. */}
        <Route path="/shopify" element={<LegacySettingsRedirect target="/app" />} />

        {/* Public referral entry points — no AppShell, no auth */}
        <Route path="/r/:code/welcome" element={<ReferralWelcome />} />
        <Route path="/r/:code" element={<ReferralSignup />} />

        {/* One authenticated layout keeps the pending OAuth state alive across
            /app and /onboarding. The provider remains above every existing
            email, onboarding and billing gate without changing their order. */}
        <Route element={<AuthenticatedRoutes />}>
          {/* /app — nested: index → new Dashboard via AppShell, /app/* → MainApp for un-ported screens */}
          <Route path="/app" element={
            <RequireVerifiedEmail fallback={<LoadingScreen />}>
            <RequireOnboarding>
              <RequireActiveBilling>
              <BrandProvider>
              <GenerationStatusProvider>
                <ContentFeedProvider>
                  <GenerationStatusContentFeedBridge />
                  <ResearchActivityBridge />
                  <AddBrandChainBridge />
                  <DashboardDataProvider>
                    <UpgradeProvider>
                      {/* TASK-331: persistent grace/lapsed/cancel_scheduled notice,
                          present on every /app screen. Must sit inside both
                          DashboardDataProvider and UpgradeProvider (Renew opens
                          the shared UpgradeModal via useUpgrade(), and UpgradeModal
                          itself reads useDashboardData()) — placed OUTSIDE
                          PreviewProvider so it is never hidden behind the preview
                          paywall blur. */}
                      {/* GH-1133 S5: in the free preview this slot carries
                          the PreviewStrip instead (§2.0 C7). */}
                      <NotInFreePreview preview={<PreviewStrip />}>
                        <BillingStateBanner />
                      </NotInFreePreview>
                      <PreviewProvider>
                        <ScreenTraceBridge />
                        <PostingRouteOutlet />
                        {/* TASK-144 (issue #237): recommendation nudge, sibling
                            of the routed screen inside PreviewProvider — NOT
                            part of its paywall overlay. Its own z-index (900)
                            keeps it under the paywall blur (990) so only one
                            recommendation surface shows at a time. */}
                        <NotInFreePreview><OnboardingNudge /></NotInFreePreview>
                        {/* TASK-145 (issue #238): thin product tour, sibling of
                            the routed screen and of OnboardingNudge — its own
                            z-index (950) stays under the paywall blur (990). */}
                        <NotInFreePreview><ProductTour /></NotInFreePreview>
                      </PreviewProvider>
                    </UpgradeProvider>
                  </DashboardDataProvider>
                </ContentFeedProvider>
              </GenerationStatusProvider>
              </BrandProvider>
              </RequireActiveBilling>
            </RequireOnboarding>
            </RequireVerifiedEmail>
        }>
          <Route index element={<AppShell topbarTitle={t('topbar.dashboard')}><Dashboard /></AppShell>} />
          {/* First-post reveal — deliberately chromeless (full-viewport stage). */}
          <Route path="reveal/:postId" element={<FirstPostReveal />} />
          {/* Research PDF — chromeless print sheet (inherits RequireAuth from /app). */}
          <Route path="research/print/:reportId" element={<ResearchPrint />} />
          <Route path="library" element={<AppShell topbarTitle={t('topbar.library')}><Library /></AppShell>} />
          <Route path="post/:id" element={<AppShell topbarTitle={t('topbar.post')}><PostPreview /></AppShell>} />
          <Route path="post/:id/edit" element={<AppShell topbarTitle={t('topbar.editPost')} hasStickyFooter><FreePublishingEditorRoute><EditPost /></FreePublishingEditorRoute></AppShell>} />
          <Route path="settings" element={<AppShell topbarTitle={t('topbar.settings')}><NewSettings /></AppShell>} />
          <Route path="plans" element={<AppShell topbarTitle={t('topbar.plans')}><Plans /></AppShell>} />
          <Route path="create" element={<AppShell topbarTitle={t('topbar.createPosts')} hasStickyFooter><FreePublishingEditorRoute><NewCreatePost /></FreePublishingEditorRoute></AppShell>} />
          {/* Marketing hub: the unified PageHeader (under MarketingSubnav) titles
              each page, so the global topbar carries no duplicate title here. */}
          <Route path="plan" element={<AppShell><NewContentPlan /></AppShell>} />
          <Route path="research" element={<AppShell><ResearchScreen /></AppShell>} />
          <Route path="brandkit" element={<AppShell><BrandKitScreen /></AppShell>} />
          <Route path="plan/edit-strategy" element={<AppShell topbarTitle={t('topbar.editStrategy')} hasStickyFooter><NewEditStrategy /></AppShell>} />
          {/* Legacy URL guard — any stray /app/content-plan link (from older
              code or shared bookmarks) bounces to the new /app/plan page
              instead of falling into the MainApp catch-all and showing
              the legacy ContentPlan screen. */}
          <Route path="content-plan" element={<Navigate to="/app/plan" replace />} />
          <Route path="content-plan/*" element={<Navigate to="/app/plan" replace />} />
          <Route path="calendar" element={<AppShell topbarTitle={t('topbar.calendar')}><NewCalendar /></AppShell>} />
          <Route path="messages" element={<AppShell topbarTitle={t('topbar.messages')}><NewMessages /></AppShell>} />
          <Route path="batch/:id" element={<AppShell topbarTitle={t('topbar.batchProgress')}><NewBatch /></AppShell>} />
          <Route path="referrals" element={<AppShell topbarTitle={t('topbar.referrals')} topbarSub={t('topbar.subSettingsReferrals')}><NewReferrals /></AppShell>} />
          <Route path="admin/referrals" element={<RequireAdmin><AdminShell title="Referrals"><AdminReferrals /></AdminShell></RequireAdmin>} />
          <Route path="admin/broadcast" element={<RequireAdmin><AdminShell title="Broadcast"><AdminBroadcast /></AdminShell></RequireAdmin>} />
          <Route path="admin/activation" element={<RequireAdmin><AdminShell title="Refresh activation"><AdminActivation /></AdminShell></RequireAdmin>} />
          <Route path="admin/plans" element={<RequireAdmin><AdminShell title="Plans & limits"><AdminPlans /></AdminShell></RequireAdmin>} />
          <Route path="admin/support" element={<RequireAdmin><AdminShell title="Support Hub"><AdminSupport /></AdminShell></RequireAdmin>} />
          <Route path="admin/users" element={<RequireAdmin><AdminShell title="Users"><AdminUsers /></AdminShell></RequireAdmin>} />
          <Route path="admin/behavior" element={<RequireAdmin><AdminShell title="Behavior"><AdminBehavior /></AdminShell></RequireAdmin>} />
          <Route path="admin/replay" element={<RequireAdmin><AdminShell title="Session replay"><AdminReplay /></AdminShell></RequireAdmin>} />
          {/* All un-ported MainApp screens wrap in AppShell — internal Sidebar/Topbar
              suppressed via chromeless prop so the new chrome is the only one shown. */}
          <Route path="*" element={<AppShell><MainApp chromeless /></AppShell>} />
          </Route>

          <Route path="/*" element={
            <RequireVerifiedEmail fallback={<LoadingScreen />}>
            <ContentFeedProvider>
              <DashboardDataProvider>
                <UpgradeProvider>
                  <MainApp />
                </UpgradeProvider>
              </DashboardDataProvider>
            </ContentFeedProvider>
            </RequireVerifiedEmail>
          } />
        </Route>
      </Routes>
      </UploadProgressProvider>
    </BrowserRouter>
  );
}
